import {Exclude, Expose, instanceToPlain, plainToInstance} from "class-transformer";
import * as t from "io-ts";
import {validateJSON, validateJSONNow} from "../web/validation";
import {Entity} from "./entity";

/**
 * One team a person belongs to, with that person's own membership, from
 * `GET /teams/memberships/{personId}`. The team's other members are not included.
 */
export const TeamMembershipCodec = t.intersection([
  t.type({
    teamId: t.string,
    teamName: t.string,
    memberId: t.string,
  }),
  t.partial({
    teamDescription: t.string,
    scopeType: t.string,
    scopeObjectName: t.string,
    parentTeamName: t.string,
    teamEnabled: t.boolean,
    enabled: t.boolean,
    effectiveStartDttm: t.number,
    effectiveEndDttm: t.number,
  }),
]);

export const TeamMembershipArrayCodec = t.array(
  TeamMembershipCodec
);

export type TeamMembership = t.TypeOf<typeof TeamMembershipCodec>;
export type TeamMembershipArray = t.TypeOf<typeof TeamMembershipArrayCodec>;

@Exclude()
export class TeamMembershipEntity implements Entity {
  /** The membership's own id, which the server sends as `memberId`. */
  @Expose({name: "memberId"})
  id!: string;

  @Expose()
  teamId!: string;

  @Expose()
  teamName!: string;

  @Expose()
  teamDescription?: string;

  @Expose()
  scopeType?: string;

  /** What the team covers, e.g. a region or company. */
  @Expose()
  scopeObjectName?: string;

  @Expose()
  parentTeamName?: string;

  @Expose()
  teamEnabled?: boolean;

  @Expose()
  enabled?: boolean;

  @Expose()
  effectiveStartDttm?: number;

  @Expose()
  effectiveEndDttm?: number;

  // The server's ObjectTypeId for a team membership.
  readonly entityType = 'TEAM_MEMBER';

  get displayName(): Readonly<string> {
    return this.teamName;
  }

  /**
   * Constructor for TeamMembershipEntity. Recommend using the {@link from} method instead.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static from(value: TeamMembership): TeamMembershipEntity {
    return plainToInstance(TeamMembershipEntity, value, {
      excludeExtraneousValues: true,
    });
  }

  loadTypeValue(): Promise<TeamMembership> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(TeamMembershipCodec, object);
  }

  asTypeValue(): TeamMembership {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSONNow(TeamMembershipCodec, object);
  }
}
